/**
 * 이상한 방 - 사물함(보유 · 장착 · 환급)
 *
 * 근거 문서: 로드맵 확정안 3.2절(가치 0인 봉투는 존재하지 않는다 · 꾸밈은 규칙 영향 0) · 5절(어휘: 사물함)
 *           `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제 - 탄창 상한은 `magazines.ts`)
 *
 * ## 산출물을 "적용"하는 자리는 여기 하나다
 *
 * 봉투가 준 것이 계정에 어떻게 반영되는가 - 새 꾸밈이면 보유 목록에, 중복이면 핀으로, 탄창이면 상한 안에서
 * 계정 탄창으로, 넘치면 핀으로 - 그 판단이 전부 `applyReward` 하나에 있다. 연출 코드가 "중복이면 25핀"을
 * 따로 계산하기 시작하면 표(`envelopeTable.ts`)를 고친 날 화면과 저장이 다른 값을 말한다.
 *
 * ## 이 파일은 탄창을 **세지 않는다**
 *
 * 계정 탄창의 진실은 `magazines.ts`·`accountMagazines.ts`다. 여기서는 "몇 개를 넣어야 하는가"와
 * "몇 개가 넘쳐서 핀이 되는가"를 계산해 돌려줄 뿐이고, 실제 지급은 `accountMagazines.grant(count, 'envelope')`가
 * 한다. 상한 계산을 두 벌 만들지 않기 위해 `MAX_MAGAZINE_COUNT`만 빌려 온다.
 *
 * ## 장착은 소유를 넘지 못한다
 *
 * `equipLook`은 가진 것만 끼운다. 기본 꾸밈은 누구나 가진 것이므로 언제나 끼울 수 있다.
 * 저장값이 "안 가진 것을 끼운" 상태로 들어오면(`parseInventory`) 그 자리는 기본값으로 되돌린다 -
 * 저장 조작으로 SSR 표식을 끼우는 경로가 여기서 닫힌다(로컬 저장이라 완전한 방어는 아니다. `magazines.ts`
 * 머리말이 같은 한계를 말한다).
 */

import { MAX_MAGAZINE_COUNT } from '../magazines'
import { ENVELOPE_TABLE, type EnvelopeTable, type Grade, type Reward } from './envelopeTable'
import { KIT_IDS, isKitId, type KitId } from './kits'
import {
  DEFAULT_LOOKS,
  LOOK_SLOTS,
  completionLooks,
  getLook,
  isLookId,
  obtainableLooks,
  type LookId,
  type LookSlot,
} from './looks'
import { completionNicknames, isNicknameId, obtainableNicknames, type NicknameId } from './titles'

/* --- 타입 ------------------------------------------------------------------- */

/** 지금 끼운 것. 꾸밈은 자리마다 하나, 별명은 없을 수 있다(기본은 "별명 없음"이다). */
export type Equipped = Readonly<Record<LookSlot, LookId> & { nickname: NicknameId | null }>

export type Inventory = Readonly<{
  /** 봉투에서 얻은 꾸밈. 기본 꾸밈은 여기 없어도 가진 것이다(`ownsLook`). */
  looks: readonly LookId[]
  nicknames: readonly NicknameId[]
  /** 준비물 보유 수. 넷 전부 키가 있고 0일 수 있다. */
  kits: Readonly<Record<KitId, number>>
  equipped: Equipped
}>

/** 산출물 하나를 계정에 반영한 결과. 저장은 호출자가 한 덩어리로 한다. */
export type RewardOutcome = Readonly<{
  inventory: Inventory
  /** `accountMagazines.grant`에 넣을 수. 상한을 넘는 분량은 이미 뺀 값이라 0일 수 있다. */
  magazinesToGrant: number
  /** 핀에 더할 양 = 핀 산출물 + 중복 환급 + 탄창 초과 환급. */
  pinsToAdd: number
  /** 이미 가진 꾸밈·별명이었는가. 연출이 "이미 있어서 핀 25로" 한 줄을 더 붙이는 근거다. */
  duplicate: boolean
  /** 상한에 걸려 핀이 된 탄창 수. */
  overflowMagazines: number
}>

/* --- 생성 ------------------------------------------------------------------- */

const ZERO_KITS: Readonly<Record<KitId, number>> = Object.fromEntries(KIT_IDS.map((id) => [id, 0])) as Readonly<
  Record<KitId, number>
>

/** 아무것도 뽑지 않은 사람의 사물함. 기본 꾸밈이 자리마다 끼워져 있고 별명은 없다. */
export function createInventory(): Inventory {
  return {
    looks: [],
    nicknames: [],
    kits: ZERO_KITS,
    equipped: { ...DEFAULT_LOOKS, nickname: null },
  }
}

/* --- 조회 -------------------------------------------------------------------- */

/** 기본 꾸밈은 언제나 가진 것이다. 완주 보상은 앉기 전까지 안 가진 것이다(`settleCompletion`). */
export function ownsLook(inventory: Inventory, id: LookId): boolean {
  return getLook(id).source === 'default' || inventory.looks.includes(id)
}

export function ownsNickname(inventory: Inventory, id: NicknameId): boolean {
  return inventory.nicknames.includes(id)
}

export function kitCount(inventory: Inventory, id: KitId): number {
  return inventory.kits[id]
}

/* --- 수집률 ------------------------------------------------------------------- */

/**
 * 한 갈래의 수집 진도. `owned`는 언제나 `total` 이하다.
 *
 * **`total`은 봉투에서 나오는 것만 센다.** 기본 꾸밈 다섯은 누구나 가지고 있고 봉투 풀에도 없으므로
 * (`looks.ts` 머리말) 분자와 분모에 함께 넣으면 아무것도 안 뽑은 사람이 15%에서 시작한다. 그건 진도가
 * 아니라 부풀린 수다 - 이 저장소가 바늘·확률·등급에서 지켜 온 것과 같은 규칙이다(화면은 사실을 말한다).
 * 준비물도 세지 않는다: 쓰면 없어지는 것이라 "모았다"가 성립하지 않는다(수집률이 줄어드는 진도가 된다).
 *
 * **완주 보상도 세지 않는다.** 그것을 주는 조건이 이 수가 100%가 되는 것이라, 분모에 넣으면 조건이
 * 자기 자신을 가리킨다 - 마지막 한 장을 뽑아도 29분의 29가 아니라 35분의 29가 되어 100%가 영영 오지 않는다.
 */
export type CollectionCount = Readonly<{ owned: number; total: number }>

/** 사물함 전체의 수집 진도. 갈래마다 하나씩과, 둘을 합친 것. */
export type Collection = Readonly<{
  looks: CollectionCount
  nicknames: CollectionCount
  /** 화면 맨 위의 한 줄이 쓰는 합. 갈래를 더한 값이지 따로 세는 값이 아니다. */
  all: CollectionCount
}>

function countOwned(inventory: Inventory, ids: readonly LookId[]): CollectionCount {
  return { owned: ids.filter((id) => ownsLook(inventory, id)).length, total: ids.length }
}

/** 이 자리의 꾸밈 진도. 사물함이 자리마다 한 구획을 그리므로 그 제목 옆에 붙는다. */
export function lookCollectionIn(inventory: Inventory, slot: LookSlot): CollectionCount {
  return countOwned(
    inventory,
    obtainableLooks()
      .filter((look) => look.slot === slot)
      .map((look) => look.id),
  )
}

export function collectionOf(inventory: Inventory): Collection {
  const looks = countOwned(inventory, obtainableLooks().map((look) => look.id))
  const obtainable = obtainableNicknames()
  const nicknames: CollectionCount = {
    owned: obtainable.filter((nick) => ownsNickname(inventory, nick.id)).length,
    total: obtainable.length,
  }
  return {
    looks,
    nicknames,
    all: { owned: looks.owned + nicknames.owned, total: looks.total + nicknames.total },
  }
}

/**
 * 화면에 적을 백분율(0~100 정수).
 *
 * **다 모으지 않았는데 100이 나오지 않는다.** 반올림하면 마지막 한 장을 남긴 사람에게 "100%"라고 말하게
 * 되고, 그 한 줄이 거짓이면 수집률 전체가 장식이 된다. 그래서 내림이고 99에서 한 번 더 막는다.
 * 반대쪽(하나를 모았는데 0%)은 사실이라 그대로 둔다 - 29분의 1은 실제로 3%다.
 */
export function collectionPercent(count: CollectionCount): number {
  if (count.total <= 0) return 0
  if (count.owned >= count.total) return 100
  return Math.min(99, Math.floor((count.owned / count.total) * 100))
}

/* --- 완주 보상 ------------------------------------------------------------------ */

/** 봉투에서 나오는 것을 전부 모았는가. 빈 표(분모 0)는 "다 모았다"가 아니다 - 아직 모을 것이 없는 것이다. */
export function isCollectionComplete(inventory: Inventory): boolean {
  const all = collectionOf(inventory).all
  return all.total > 0 && all.owned >= all.total
}

/**
 * 다 모은 사람에게 완주 보상을 앉힌다. 조건이 아니면, 또는 이미 다 앉았으면 **같은 참조**를 돌려준다
 * (`equipLook`이 거절을 알리는 방식과 같다 - 호출자는 참조 비교로 "아무 일도 없었다"를 안다).
 *
 * ## 왜 지급하는 자리가 이 함수 하나인가
 *
 * 부르는 곳이 둘이다: 산출물을 적용한 직후(`applyReward`)와 저장값을 읽은 직후(`parseInventory`).
 * 둘째가 없으면 이 기능이 생기기 **전에** 이미 다 모은 사람은 영영 못 받는다 - 그 사람에게는 더 뽑을
 * 봉투가 없어 첫째 경로가 다시 오지 않기 때문이다. 그래서 "다 모았으면 가지고 있다"를 지급 사건이 아니라
 * 사물함의 **불변식**으로 두고, 사물함이 만들어지는 두 입구에서 함께 세운다.
 *
 * 되돌리는 경로는 없다. 수집률은 줄지 않으므로(잃는 산출물이 없다) 한 번 선 불변식은 깨지지 않는다.
 */
export function settleCompletion(inventory: Inventory): Inventory {
  if (!isCollectionComplete(inventory)) return inventory

  const looks = completionLooks()
    .map((look) => look.id)
    .filter((id) => !inventory.looks.includes(id))
  const nicknames = completionNicknames()
    .map((nick) => nick.id)
    .filter((id) => !inventory.nicknames.includes(id))
  if (looks.length === 0 && nicknames.length === 0) return inventory

  return {
    ...inventory,
    looks: [...inventory.looks, ...looks],
    nicknames: [...inventory.nicknames, ...nicknames],
  }
}

/* --- 산출물 적용 ---------------------------------------------------------------- */

/**
 * 봉투 산출물 하나를 사물함에 반영한다.
 *
 * `grade`를 따로 받는 이유는 환급액이 **뽑힌 등급**에서 나오기 때문이다. 산출물 자체에 등급을 적어 두지 않는
 * 것은 표가 두 번 말하지 않게 하려는 것(`titles.ts` 머리말)이고, `DrawResult`가 등급을 이미 들고 있다.
 *
 * `magazinesHeld`는 지금 계정 탄창 수다. 상한(99)까지 남은 자리만큼만 지급하고 나머지는 핀이다 -
 * 이것이 "가치 0인 봉투 없음"의 탄창 쪽 절반이다(꾸밈·별명 쪽 절반은 중복 환급).
 */
export function applyReward(
  inventory: Inventory,
  reward: Reward,
  grade: Grade,
  magazinesHeld: number,
  table: EnvelopeTable = ENVELOPE_TABLE,
): RewardOutcome {
  const outcome = applyOne(inventory, reward, grade, magazinesHeld, table)
  /*
   * 마지막 한 장이 들어온 그 순간 완주 보상도 함께 앉는다. 호출자가 따로 부르게 두지 않는 이유는
   * "산출물을 적용하는 자리는 여기 하나다"(머리말)이기 때문이다 - 지급이 바깥에 있으면 열 장 뽑기의
   * 다섯째 장에서 조건이 찼는데 저장은 열째 장 뒤에 한 번 되는 식으로 두 사실이 어긋난다.
   */
  const settled = settleCompletion(outcome.inventory)
  return settled === outcome.inventory ? outcome : { ...outcome, inventory: settled }
}

/** 산출물 갈래별 계산. 완주 보상을 모르는 순수 한 겹이다(위가 그것을 얹는다). */
function applyOne(
  inventory: Inventory,
  reward: Reward,
  grade: Grade,
  magazinesHeld: number,
  table: EnvelopeTable,
): RewardOutcome {
  const unchanged = { inventory, magazinesToGrant: 0, pinsToAdd: 0, duplicate: false, overflowMagazines: 0 }

  switch (reward.kind) {
    case 'magazines': {
      const room = Math.max(0, MAX_MAGAZINE_COUNT - Math.max(0, Math.floor(magazinesHeld)))
      const granted = Math.min(reward.count, room)
      const overflow = reward.count - granted
      return {
        ...unchanged,
        magazinesToGrant: granted,
        overflowMagazines: overflow,
        pinsToAdd: overflow * table.magazineOverflowRefund,
      }
    }
    case 'pins':
      return { ...unchanged, pinsToAdd: reward.amount }
    case 'kit':
      return {
        ...unchanged,
        inventory: {
          ...inventory,
          kits: { ...inventory.kits, [reward.kitId]: inventory.kits[reward.kitId] + reward.count },
        },
      }
    case 'look':
      if (ownsLook(inventory, reward.lookId)) {
        return { ...unchanged, duplicate: true, pinsToAdd: table.refund[grade] }
      }
      return { ...unchanged, inventory: { ...inventory, looks: [...inventory.looks, reward.lookId] } }
    case 'nickname':
      if (ownsNickname(inventory, reward.nicknameId)) {
        return { ...unchanged, duplicate: true, pinsToAdd: table.refund[grade] }
      }
      return { ...unchanged, inventory: { ...inventory, nicknames: [...inventory.nicknames, reward.nicknameId] } }
  }
}

/**
 * 산출물이 사람에게 **무언가를 남겼는가.** "가치 0인 봉투 없음"의 판정식이고 테스트가 풀 전체에 돌린다.
 * 남긴 것은 넷 중 하나다 - 탄창이 들어갔다 · 핀이 늘었다 · 사물함에 새 것이 생겼다 · 준비물이 늘었다.
 */
export function hasValue(before: Inventory, outcome: RewardOutcome): boolean {
  if (outcome.magazinesToGrant > 0 || outcome.pinsToAdd > 0) return true
  const after = outcome.inventory
  if (after.looks.length > before.looks.length || after.nicknames.length > before.nicknames.length) return true
  return KIT_IDS.some((id) => after.kits[id] > before.kits[id])
}

/* --- 장착 -------------------------------------------------------------------- */

/**
 * 꾸밈을 끼운다. 자리는 꾸밈 정의에서 온다(호출자가 자리를 따로 말하면 어긋날 수 있다).
 * 안 가진 것이면 **같은 참조**를 돌려준다 - 호출자는 참조 비교로 거부를 안다.
 */
export function equipLook(inventory: Inventory, id: LookId): Inventory {
  if (!ownsLook(inventory, id)) return inventory
  const slot = getLook(id).slot
  if (inventory.equipped[slot] === id) return inventory
  return { ...inventory, equipped: { ...inventory.equipped, [slot]: id } }
}

/** 별명을 끼운다. `null`은 "별명 없음"으로 되돌리는 것이고 언제나 허락된다. */
export function equipNickname(inventory: Inventory, id: NicknameId | null): Inventory {
  if (id !== null && !ownsNickname(inventory, id)) return inventory
  if (inventory.equipped.nickname === id) return inventory
  return { ...inventory, equipped: { ...inventory.equipped, nickname: id } }
}

/**
 * 준비물 하나를 쓴다(단계 안내 화면의 3택1, M9). 없으면 `ok: false`이고 상태는 그대로다.
 * 소비는 여기서만 일어난다 - 라운드 튜닝에 얹는 것은 `kits.withKit`이고 그쪽은 개수를 모른다.
 */
export function useKit(inventory: Inventory, id: KitId): Readonly<{ inventory: Inventory; ok: boolean }> {
  if (inventory.kits[id] <= 0) return { inventory, ok: false }
  return { inventory: { ...inventory, kits: { ...inventory.kits, [id]: inventory.kits[id] - 1 } }, ok: true }
}

/* --- 저장값 읽기 -------------------------------------------------------------- */

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function uniqueIds<T extends string>(value: unknown, guard: (v: unknown) => v is T): readonly T[] {
  if (!Array.isArray(value)) return []
  const ids: T[] = []
  for (const entry of value) {
    if (guard(entry) && !ids.includes(entry)) ids.push(entry)
  }
  return ids
}

/**
 * 저장값 → 사물함. **어떤 입력에도 던지지 않는다.** 읽을 수 없으면 빈 사물함이다.
 *
 * 표에서 사라진 꾸밈·별명 id는 조용히 떨어진다(옛 저장이 정상 입력이다). 끼운 것이 안 가진 것이면
 * 기본값으로 되돌린다 - 그 자리에 낯선 id가 남아 있으면 화면이 없는 꾸밈을 그리려 한다.
 */
export function parseInventory(value: unknown): Inventory {
  if (!isRecordLike(value)) return createInventory()

  const looks = uniqueIds(value['looks'], isLookId)
  const nicknames = uniqueIds(value['nicknames'], isNicknameId)

  const rawKits = value['kits']
  const kits: Record<KitId, number> = { ...ZERO_KITS }
  if (isRecordLike(rawKits)) {
    for (const id of KIT_IDS) {
      const count = rawKits[id]
      if (typeof count === 'number' && Number.isFinite(count) && count > 0) kits[id] = Math.floor(count)
    }
  }

  /*
   * 끼운 것을 읽기 **전에** 완주 보상을 세운다. 뒤에 세우면 "다 모은 사람이 완주 표식을 끼운 저장"이
   * 들어왔을 때 그 줄이 아직 안 가진 것으로 보여 기본값으로 되돌아간다 - 다음 저장에서 그 선택이 사라진다.
   */
  const base: Inventory = settleCompletion({
    looks,
    nicknames,
    kits,
    equipped: { ...DEFAULT_LOOKS, nickname: null },
  })

  const rawEquipped = value['equipped']
  if (!isRecordLike(rawEquipped)) return base

  let inventory = base
  for (const slot of LOOK_SLOTS) {
    const id = rawEquipped[slot]
    // 자리와 꾸밈의 자리가 다르면(저장 조작·옛 포맷) 그 줄은 무시한다. `equipLook`이 자리를 정의에서 읽는다.
    if (isLookId(id) && getLook(id).slot === slot) inventory = equipLook(inventory, id)
  }
  const nickname = rawEquipped['nickname']
  if (isNicknameId(nickname)) inventory = equipNickname(inventory, nickname)

  return inventory
}
